<template>
    <!-- Account -->
    <dividers-generic
    icon="account-balance"
    :applyBorder="false"
    :title="t('savings.products.constitute.step2.accountTitle')"/>
    <text-block-generic
    :params="{
        label: t('savings.products.constitute.step2.accountLabel'),
        content: accountFormatHelper(accountsBalances.find(ele => ele.id === formData.accountId).accountNumber)
    }"/>
    <!-- Product -->
    <dividers-generic
    icon="coin-drop"
    :title="t('savings.products.constitute.step2.productTitle')"/>
    <text-block-generic
    :params="{
        label: t('savings.products.constitute.step2.productLabel'),
        content: selectedProduct.name
    }"/>
    <text-block-generic
    :params="{ content: selectedProduct.description }"/>
    <div
    class="product-option"
    :class="{'mobile': isMobile}">
        <text-block-generic
        :style="{'width': isMobile ? '100%' : '64px'}"
        :params="{
            label: t('savings.products.constitute.step2.productDuration'),
            content: t('savings.products.constitute.step2.days', {days: productInterestRates.find(el => el.id === formData.productSelected.id).maxDuration})
        }"/>
        <text-block-generic
        :style="{'width': isMobile ? '100%' : '44px'}"
        :params="{
            label: t('savings.products.constitute.step2.productInterestRate'),
            content: productInterestRates.find(el => el.id === formData.productSelected.id).interessRate + '%'
        }"/>
        <text-block-generic
        :style="{'width': isMobile ? '100%' : 'calc((100% - 188px) / 2)'}"
        :params="{
            label: t('savings.products.constitute.step2.minAmount'),
            content: amountFormat(productInterestRates.find(el => el.id === formData.productSelected.id).minValue, currency)
        }"/>
        <text-block-generic
        :style="{'width': isMobile ? '100%' : 'calc((100% - 188px) / 2)'}"
        :params="{
            label: t('savings.products.constitute.step2.maxAmount'),
            content: amountFormat(productInterestRates.find(el => el.id === formData.productSelected.id).maxValue, currency)
        }"/>
    </div>
    <!-- Amount -->
    <dividers-generic
    icon="bills"
    :title="t('savings.products.constitute.step2.dividerAmountTitle')"/>
    <text-block-generic
    :params="{
        label: t('savings.products.constitute.step2.producApplyAmountLabel'),
        content: amountFormat(formData.amount, currency)
    }"/>
    <!-- Simulation Results -->
    <dividers-generic
    v-if="formData.simulationIsDone"
    icon="pig"
    :title="t('savings.products.constitute.step2.simulateTitle')"/>
    <div
    v-if="formData.simulationIsDone"
    class="simulation-details"
    :class="{mobile: isMobile}">
        <text-block-generic
        :params="{
            label: t('savings.products.constitute.step2.anualTaxLabel'),
            content: formData.simulation.interest + '%'
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.products.constitute.step2.totalInterestLabel'),
            content: amountFormat(formData.simulation.totalInterestCounted, currency)
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.products.constitute.step2.totalNetInterestLabel'),
            content: amountFormat(formData.simulation.totalNetInterestCounted, currency)
        }"/>
        <text-block-generic
        :params="{
            label: t('savings.products.constitute.step2.retentionLabel'),
            content: amountFormat(formData.simulation.totalTaxWithoutInterestCounted, currency)
        }"/>
    </div>
    <text-block-generic
    v-if="formData.simulationIsDone"
    :params="{
        label: t('savings.products.constitute.step2.totalAmount'),
        content: amountFormat(formData.simulation.totalAmount, currency)
    }"/>
    <!-- Divider -->
    <dividers-generic icon="pencil-line" :title="t('savings.products.constitute.step2.conditionsTitle')"/>
    <!-- CheckBox for terms -->
    <selector-checkbox
    :label="acceptConditionsParams.label"
    :selected="formData.acceptConditions"
    :errorState="acceptConditionsParams.errorState"
    :errorMessage="acceptConditionsParams.errorMessage"
    @clicked="[emit('formDataChange', { field: 'acceptConditions' , inputValue: !formData.acceptConditions}), clearFormErrors('acceptConditions')]"/>
    <div id="acceptConditions"></div>
</template>

<script setup>
import DividersGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import SelectorCheckbox from '@/components/generic/selectors/checkbox/Checkbox'

/* Vue */
import { ref, watch } from 'vue'

/* Assets and Helpers */
import { accountFormatHelper } from '@/assets/scripts/strings'
import { amountFormat } from '@/assets/scripts/amounts'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['scrollTo', 'formDataChange', 'resetShowMandatoryErrorsProp', 'formErrorsCleared'])

/* props received
@selectedProduct {Object} - selected product to subscribe
@mandatoryList { Array } - Array of mandatory keys corresponding with our form
@showMandatoryErrors - { Boolean } - Prop to trigger display of mandatory errors
@formData { Object } - object with info for our current form
@currency { String }
@isMobile: Boolean { Boolean }
*/
const props = defineProps({
    selectedProduct: Object,
    productInterestRates: Array,
    showMandatoryErrors: Boolean,
    formData: Object,
    accountsBalances: Array,
    currency: String,
    isMobile: Boolean
})

const acceptConditionsParams = ref({
    label: t('savings.products.constitute.step2.conditions', { link: props.selectedProduct.infoSheetUrl, target: '_blank' }),
    errorState: false,
    errorMessage: null
})

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
    acceptConditionsParams: acceptConditionsParams.value
})

watch(() => props.showMandatoryErrors, (newValue, oldValue) => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
     emit('scrollTo', getElementOffsetTopPosition('acceptConditions'))

    acceptConditionsParams.value.errorMessage = t('mandatoryFieldError')
    acceptConditionsParams.value.errorState = true
}

/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, (newValue, oldValue) => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

emit('scrollTo', 0)
</script>

<style lang="scss" scoped>
.product-option {
    width: 100%;
    height: 72px;

    display: flex;
    align-items: center;
    gap: 16px;

    &.mobile {
        height: auto;

        flex-direction: column;
        align-items: start;
    }
}

.simulation-details {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .text-blocks-wrapper {
        max-width: 48%;
    }

    &.mobile {
        flex-direction: column;
        gap: 24px;

        .text-blocks-wrapper {
            max-width: 100%;
        }
    }
}
</style>
